import { nowIso, REDACTED_PATHS, REDACTION_PLACEHOLDER, scrubPanLikeText } from '@ibe/utils';

/**
 * The portal's logger.
 *
 * `no-console` is an error across the whole repository, and this is what to
 * use instead. Three properties make it worth having rather than reaching for
 * `console.warn` and an eslint-disable:
 *
 * - **Structured, not formatted.** A record with fields can be shipped to a
 *   collector and queried. Retrofitting structure onto printf-style calls is a
 *   rewrite, so it is done from the start.
 * - **Redacted unconditionally**, using the same Luhn-checked scrub the
 *   services use, from `@ibe/utils`. PCI DSS admits no debug exemption, and a
 *   browser console is a log destination like any other.
 * - **It cannot break a render.** A throwing sink is swallowed. Logging is
 *   diagnostic; a collector being down must not take a screen with it.
 *
 * The default sink writes to the console, which is the one legitimate place
 * for it — hence the single eslint-disable in the application.
 */

export const LOG_LEVELS = ['debug', 'info', 'warn', 'error'] as const;
export type LogLevel = (typeof LOG_LEVELS)[number];

const LEVEL_ORDER: Readonly<Record<LogLevel, number>> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

export interface LogRecord {
  readonly level: LogLevel;
  readonly message: string;
  readonly timestamp: string;
  /** Per-call fields. */
  readonly context: Readonly<Record<string, unknown>>;
  /** Fields fixed by `child()`, e.g. the correlation id. */
  readonly bindings: Readonly<Record<string, unknown>>;
}

export type LogSink = (record: LogRecord) => void;

export interface BrowserLogger {
  debug: (message: string, context?: Readonly<Record<string, unknown>>) => void;
  info: (message: string, context?: Readonly<Record<string, unknown>>) => void;
  warn: (message: string, context?: Readonly<Record<string, unknown>>) => void;
  error: (message: string, context?: Readonly<Record<string, unknown>>) => void;
  /** A logger with fields fixed, so a screen sets a correlation id once. */
  child: (bindings: Readonly<Record<string, unknown>>) => BrowserLogger;
}

export interface BrowserLoggerOptions {
  readonly level: LogLevel;
  readonly sink?: LogSink;
  readonly bindings?: Readonly<Record<string, unknown>>;
}

const REDACTED = new Set(REDACTED_PATHS.map((path) => path.split('.').pop() ?? path));

/**
 * Removes sensitive values from a context object.
 *
 * Keyed on the leaf name from `REDACTED_PATHS` rather than on a full path,
 * because a browser context is a flat bag of fields rather than pino's nested
 * request object. One level deep is enough for what a screen logs, and
 * anything deeper is scrubbed as text by `scrubPanLikeText`.
 */
function redactContext(
  context: Readonly<Record<string, unknown>>,
): Readonly<Record<string, unknown>> {
  const safe: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(context)) {
    if (REDACTED.has(key)) {
      safe[key] = REDACTION_PLACEHOLDER;
    } else if (typeof value === 'string') {
      safe[key] = scrubPanLikeText(value);
    } else {
      safe[key] = value;
    }
  }

  return safe;
}

/**
 * The default sink.
 *
 * The one place in the application permitted to touch the console, which is
 * why the disable is here and nowhere else. Errors and warnings go to their
 * matching console methods so the browser's own filtering still works.
 */
const consoleSink: LogSink = (record) => {
  /* eslint-disable no-console --
     This is the logger's own output. Everywhere else `no-console` stands.
     Disabled for the block because the write target is chosen by level, so
     the console access and the call are on separate lines. */
  const write =
    record.level === 'error' || record.level === 'warn'
      ? console[record.level].bind(console)
      : console.log.bind(console);
  write(`[${record.level}] ${record.message}`, { ...record.bindings, ...record.context });
  /* eslint-enable no-console */
};

export function createBrowserLogger(options: BrowserLoggerOptions): BrowserLogger {
  const sink = options.sink ?? consoleSink;
  const bindings = options.bindings ?? {};
  const threshold = LEVEL_ORDER[options.level];

  const emit = (
    level: LogLevel,
    message: string,
    context: Readonly<Record<string, unknown>> = {},
  ): void => {
    if (LEVEL_ORDER[level] < threshold) return;

    try {
      sink({
        level,
        message: scrubPanLikeText(message),
        timestamp: nowIso(),
        context: redactContext(context),
        bindings,
      });
    } catch {
      // Deliberately silent. A failing sink is not worth an exception in a
      // render path, and reporting it would need a working sink.
    }
  };

  return {
    debug: (message, context) => {
      emit('debug', message, context);
    },
    info: (message, context) => {
      emit('info', message, context);
    },
    warn: (message, context) => {
      emit('warn', message, context);
    },
    error: (message, context) => {
      emit('error', message, context);
    },
    child: (childBindings) =>
      createBrowserLogger({
        ...options,
        bindings: { ...bindings, ...childBindings },
      }),
  };
}

/**
 * The application's logger.
 *
 * `debug` in development, `warn` in production: an operator's console should
 * carry what went wrong and nothing else, and a chatty production log is how
 * the one line that mattered gets scrolled past.
 */
export const logger = createBrowserLogger({
  level: import.meta.env.DEV ? 'debug' : 'warn',
});
